<template>
  <article
    class="text-sm space-y-2"
    :data-test-id="`customer-detailed-reviews-${review.reviewId}`"
  >
    <div :data-test-id="`customer-reviews-section-${review.reviewId}`">
      <vf-rating
        class="mb-2"
        :data-test-id="`customer-rating-${review.reviewId}`"
        size="xs"
        :value="review.metrics.rating || (review.media?.[0]?.rating || 0)"
      />
      <p
        class="text-base title-5 overflow-wrap-anywhere"
        :data-test-id="`review-headline-${review.reviewId}`"
      >
        {{ isTranslated ? translatedReview.translated_headline : review.details.headline }}
      </p>
      <div
        class="mt-2 flex items-center text-xs c-grey-20"
        :data-test-id="`reviewer-details-${review.reviewId}`"
      >
        <div
          v-if="review.details.nickname"
          class="flex items-center"
          :data-test-id="`reviewer-name-${review.reviewId}`"
        >
          <span class="line-clamp-1 overflow-wrap-anywhere lg:line-clamp-2">{{ review.details.nickname }}</span>
          <div class="mx-2 h-3 b-r b-grey-20" />
        </div>
        <div
          v-if="review.details.location"
          class="flex items-center"
          :data-test-id="`reviewer-location-${review.reviewId}`"
        >
          <span class="line-clamp-1 overflow-wrap-anywhere lg:line-clamp-2">{{ review.details.location }}</span>
          <div class="mx-2 h-3 b-r b-grey-20" />
        </div>
        <time :data-test-id="`reviewer-submitted-time-${review.reviewId}`">
          {{ useTimeSince(review.details.createdDate) }}
        </time>
        <p
          v-if="review.badges.isVerifiedBuyer"
          class="ml-2 flex items-center gap-1"
          :data-test-id="`reviewer-type-${review.reviewId}`"
        >
          <vf-icon class="c-gray-40" name="success" />
          <span>{{ $t.reviewVerifiedBuyer }}</span>
        </p>
        <p
          v-if="$t.reviewDisclosure?.[`${review.details.disclosureCode}`]"
          class="overflow-wrap-anywhere"
          :data-test-id="`reviewer-disclosure-${review.reviewId}`"
        >
          <span class="mx-2 h-3 b-r b-grey-20" />
          <span>{{ $t.reviewDisclosure?.[`${review.details.disclosureCode}`] }}</span>
        </p>
      </div>
    </div>
    <div v-if="review?.media?.[0]?.headline" class="c-grey-20">
      {{ review?.media[0]?.headline }}
    </div>
    <p
      class="overflow-wrap-anywhere"
      :data-test-id="`customer-review-comment-${review.reviewId}`"
    >
      <template v-if="isTranslated">
        {{ translatedReview.translated_comments }}
      </template>
      <base-interpolator
        v-else-if="searchTerm"
        :content="review.details.comments.replace(new RegExp(searchTerm, 'gi'), '{bold|$&}')"
      >
        <template #bold="{ args: [message] }">
          <b>{{ message }}</b>
        </template>
      </base-interpolator>
      <template v-else>
        {{ review.details.comments }}
      </template>
    </p>
    <div v-if="$feature.pdpReviewTranslate && isReviewTranslatable">
      <base-button v-if="!isTranslated" class="underlined" @click="handleTranslate(locale)">
        {{ $t.reviewTranslate }}
      </base-button>
      <base-button v-else class="underlined" @click="isTranslated = false">
        {{ $t.reviewTranslateBack }}
      </base-button>
    </div>
    <div v-if="review.details.merchantResponse" class="bg-grey-90 p-4">
      <p class="mb-2">
        {{ $t.reviewResponseFromBrand }}
      </p>
      <p>{{ review.details.merchantResponse }}</p>
    </div>
    <base-collapse v-if="review?.details?.properties?.length" disable-visibility-toggle>
      <template #toggle="{ expanded }">
        <div class="flex items-center">
          {{ expanded ? $t.lessDetails : $t.moreDetails }}
          <vf-icon :dir="expanded ? 'up' : 'down'" name="chevron" size="md" />
        </div>
      </template>
      <div class="grid cols-2 mt-2 gap-8 lg:cols-3">
        <div v-for="{ key, label, value } in review.details.properties" :key>
          <p class="bg-grey-80 px-2 py-1">
            {{ label }}
          </p>
          <ul class="px-2 py-1 space-y-2">
            <li v-for="entry in value" :key="entry">
              {{ entry }}
            </li>
          </ul>
        </div>
      </div>
    </base-collapse>
    <div
      v-if="review.details.bottomLine"
      :data-test-id="`customer-review-bottom-line-recommendation-${review.reviewId}`"
    >
      <p class="fw-bold">
        {{ $t.reviewBottomLine }}
      </p>
      <p v-if="review.details.bottomLine === 'Yes'">
        {{ $t.reviewBottomLinePositive }}
      </p>
      <p v-if="review.details.bottomLine === 'No'">
        {{ $t.reviewBottomLineNegative }}
      </p>
    </div>
    <div class="flex items-center" :data-test-id="`customer-review-helpful-section-${review.reviewId}`">
      <base-button
        :aria-label="replaceAll($pluralize($t.reviewVoteHelpful, yesVotes), { count: Math.min(yesVotes, 99) })"
        class="flex items-center gap-1"
        @click="handleVote({ voteType: 'helpful' })"
      >
        <vf-icon name="thumbs-up" size="md" />
        {{ Math.min(yesVotes, 99) }}
      </base-button>
      <base-button
        :aria-label="replaceAll($pluralize($t.reviewVoteNotHelpful, noVotes), { count: Math.min(noVotes, 99) })"
        class="ml-2 flex items-center gap-1"
        @click="handleVote({ voteType: 'unhelpful' })"
      >
        <vf-icon dir="left" name="thumbs-up" size="md" />
        {{ Math.min(noVotes, 99) }}
      </base-button>
      <div class="flex-1" />
      <vf-link
        v-if="!flagged"
        class="ml-4 text-xs capitalize"
        @click="DialogReportIssue.keep().open()"
      >
        {{ $t.reviewFlagCTA }}
      </vf-link>
      <p v-else class="ml-4 text-xs nowrap">
        {{ $t.youFlaggedReview }}
      </p>
    </div>
    <dialog-report-issue :review-id="review.reviewId" @flagged="flagged = true" />
  </article>
</template>

<script lang="ts" setup>
import type { ProductReviewsReview, ProductReviewsVoteRequest } from '#root/api/clients/product/data-contracts'

const { productId, review, searchTerm = '' } = defineProps<{
  productId: string
  review: ProductReviewsReview
  searchTerm?: string
}>()

const DialogReportIssue = createDialog('report-issue')
const { $feature } = useNuxtApp()

const hasVoted = ref(false)
const yesVotes = ref(review?.metrics?.helpfulVotes)
const noVotes = ref(review?.metrics?.notHelpfulVotes)
const flagged = ref(false)
const isTranslated = ref(false)
const translatedReview = ref()
const [locale] = useLocale().split('-')

const isReviewTranslatable = computed(() => {
  const [reviewLocale] = review.details.locale.split('_')
  return reviewLocale !== locale
})

function handleVote({ voteType }: ProductReviewsVoteRequest) {
  if (!hasVoted.value) {
    useApi().products.reviewVote(productId, review.reviewId, { voteType })
    voteType === 'helpful' ? yesVotes.value++ : noVotes.value++
    hasVoted.value = true
  }
}

function handleTranslate(lang) {
  if (!isTranslated.value && $feature.pdpReviewTranslate) {
    useApi().products.$reviewTranslate(review.reviewId, { target_language: lang }).then((res) => {
      if (res.results) {
        translatedReview.value = res?.results[0].translation_document
        isTranslated.value = true
      }
    })
  }
}
</script>
